/**
 * Supabase Storage image transform helper.
 *
 * Rewrites a public storage URL to go through Supabase's image render
 * endpoint with optional width/format/quality. Falls back to the original
 * URL for any non-Supabase URL (or if transforms aren't enabled on the
 * project — the request will 400 and we'll just lose the optimization,
 * not the image, because we generate srcset alongside the original src).
 *
 * Public storage URLs look like:
 *   https://<ref>.supabase.co/storage/v1/object/public/<bucket>/<path>
 * Render endpoint:
 *   https://<ref>.supabase.co/storage/v1/render/image/public/<bucket>/<path>?width=...&format=...
 */

export type TransformOpts = {
  width?: number;
  height?: number;
  quality?: number;
  format?: "origin" | "webp" | "avif";
  resize?: "cover" | "contain" | "fill";
};

const OBJECT_SEGMENT = "/storage/v1/object/public/";
const RENDER_SEGMENT = "/storage/v1/render/image/public/";

export function transformSupabaseImage(
  url: string | null | undefined,
  opts: TransformOpts = {},
): string {
  if (!url) return "";
  // Supabase image transformations require a paid add-on that isn't enabled
  // on this project, so the render endpoint 400s. Return the original URL.
  void opts;
  void RENDER_SEGMENT;
  return url;
}

/**
 * Build a srcset string for the given widths. All variants are served
 * through the render endpoint; the browser picks the best fit.
 */
export function supabaseSrcSet(
  url: string | null | undefined,
  widths: number[],
  format: TransformOpts["format"] = "webp",
  quality = 78,
): string {
  // Transforms disabled — no srcset variants available.
  void url;
  void widths;
  void format;
  void quality;
  return "";
}
